import { MessageSquareText, PanelRightClose, Sparkles } from "lucide-react";
import type { ComponentProps, JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { NoteAssistantTarget } from "@/common/types/noteAssistant";
import { Button } from "@/components/ui/button";
import {
  type AssistantRequest,
  NoteAssistantPanel,
} from "@/components/ui/visits/NoteAssistantPanel";
import { useFeedbackStore } from "@/store/feedback.store";
import {
  AssistantSources,
  AttachSourceButton,
  SourceChips,
  type Props as SourcesProps,
  useSourcesAttaching,
} from "./sources/AssistantSources";

export type AssistantUnavailableReason =
  | "no-text"
  | "editing"
  | "old-version"
  | "generating";

const REASON_KEY = {
  "no-text": "visit.workspace.assistantNeedsNote",
  editing: "visit.workspace.assistantWhileEditing",
  "old-version": "visit.workspace.assistantOldVersion",
  generating: "visit.workspace.assistantGenerating",
} as const satisfies Record<AssistantUnavailableReason, string>;

interface Props {
  target: NoteAssistantTarget;
  // Name of the document being edited (e.g. the note template).
  documentName: string;
  unavailable?: AssistantUnavailableReason | null;
  onApplied: () => void;
  // Shown on the desktop panel only; the drawer and sheet have their own
  // close control.
  onCollapse?: () => void;
  onClose: () => void;
  // File and screenshot upload (visit sources): a paperclip in the
  // instruction box, chips under it, drop or paste anywhere in the panel.
  sources?: SourcesProps;
  // Sources selected for the assistant's proposals.
  sourceIds?: readonly number[];
  // A prepared instruction to propose at once (transcript correction).
  request?: AssistantRequest | null;
  onRequestTaken?: () => void;
}

// The note assistant as a workspace panel: same propose, fact-check diff and
// versioned apply flow as before, scoped to the document that is open.
export const WorkspaceAssistant = ({
  target,
  documentName,
  unavailable,
  onApplied,
  onCollapse,
  onClose,
  sources,
  sourceIds,
  request,
  onRequestTaken,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const panel = onCollapse !== undefined;
  const [proposalShown, setProposalShown] = useState(false);
  const content = (
    <div className="flex-1 space-y-3 px-4 pb-6 sm:px-5">
      {/* Hidden while a proposal is shown, so it fits without scrolling. */}
      {!proposalShown && (
        <p className="text-[13px] leading-5 text-muted-foreground">
          {t("visit.workspace.assistantIntro")}
        </p>
      )}
      {unavailable ? (
        <>
          <p
            className="rounded-lg border border-dashed p-3 text-sm"
            data-testid="assistant-unavailable"
            role="status"
          >
            {t(REASON_KEY[unavailable])}
          </p>
          {sources && (
            <div className="flex flex-col items-start gap-1.5">
              <AttachSourceButton showLabel />
              <SourceChips />
            </div>
          )}
        </>
      ) : (
        <ClassicAssistantPanel
          key={`${target.visitPublicId}:${target.notePublicId ?? "main"}`}
          composerAccessory={sources && <AttachSourceButton />}
          composerFooter={sources && <SourceChips />}
          embedded
          request={request}
          sourceIds={sourceIds}
          onRequestTaken={onRequestTaken}
          target={target}
          onApplied={onApplied}
          onClose={onClose}
          onProposalShownChange={setProposalShown}
        />
      )}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-assistant-root>
      <div className="flex items-start gap-3 px-4 pb-2 pt-4 sm:px-5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Sparkles aria-hidden className="size-5 text-primary" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[17px] font-semibold leading-[22px]">
            {t("visit.workspace.assistant")}
          </h2>
          <p className="truncate text-sm text-muted-foreground">
            {t("visit.workspace.editingTarget", { name: documentName })}
          </p>
        </div>
        {panel && (
          <Button
            aria-label={t("visit.workspace.feedback")}
            className="size-11 shrink-0 lg:size-9"
            data-testid="workspace-feedback"
            size="icon"
            title={t("visit.workspace.feedback")}
            type="button"
            variant="ghost"
            onClick={() => {
              useFeedbackStore
                .getState()
                .show({ visitPublicId: target.visitPublicId });
            }}
          >
            <MessageSquareText />
          </Button>
        )}
        {onCollapse && (
          <Button
            aria-label={t("visit.workspace.hideAssistant")}
            className="size-11 shrink-0 lg:size-9"
            size="icon"
            type="button"
            variant="ghost"
            onClick={onCollapse}
          >
            <PanelRightClose />
          </Button>
        )}
      </div>

      {sources ? (
        <AssistantSources {...sources}>{content}</AssistantSources>
      ) : (
        content
      )}
    </div>
  );
};

// The classic panel inside AssistantSources: a question waits until every
// picked file is attached (fast attach), as in the v3.2 Ask bar.
const ClassicAssistantPanel = (
  props: ComponentProps<typeof NoteAssistantPanel>,
): JSX.Element => (
  <NoteAssistantPanel {...props} waitingForFiles={useSourcesAttaching()} />
);
